import { Box, CssBaseline, ThemeProvider, Typography, useMediaQuery } from '@mui/material';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { App } from './App';
import { SessionProvider } from './auth/session';
import { BrandLockup } from './components/Brand';
import { ConfigMissingError, loadConfig } from './config';
import './i18n';
import { initSupabase } from './lib/supabase';
import { buildTheme } from './theme';

function Themed({ children }: { children: React.ReactNode }) {
  const dark = useMediaQuery('(prefers-color-scheme: dark)');
  const theme = useMemo(() => buildTheme(dark ? 'dark' : 'light'), [dark]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

function ConfigError({ missing }: { missing: string[] }) {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 4 }}>
      <BrandLockup size="md" sx={{ mb: 4 }} />
      <Typography variant="h5" gutterBottom>
        {t('config.title')}
      </Typography>
      <Typography>{t('config.body', { missing: missing.join(', ') })}</Typography>
    </Box>
  );
}

const root = createRoot(document.getElementById('root')!);
try {
  const config = loadConfig();
  initSupabase(config);
  // A refused call may mean the role changed or access was removed: re-read whoami (at most every 30 s) so the
  // session provider can sign the person out instead of leaving them on screens that no longer answer.
  let checkedAt = 0;
  const recheckAccess = (error: unknown) => {
    if (Date.now() - checkedAt < 30_000) return;
    if ((error as { name?: string } | null)?.name === 'AbortError') return;
    checkedAt = Date.now();
    void queryClient.invalidateQueries({ queryKey: ['whoami'] });
  };
  const queryClient = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.queryKey[0] !== 'whoami') recheckAccess(error);
      },
    }),
    mutationCache: new MutationCache({ onError: recheckAccess }),
    defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } },
  });
  root.render(
    <StrictMode>
      <Themed>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            <App />
          </SessionProvider>
        </QueryClientProvider>
      </Themed>
    </StrictMode>,
  );
} catch (err) {
  root.render(
    <Themed>
      <ConfigError missing={err instanceof ConfigMissingError ? err.missing : ['unknown']} />
    </Themed>,
  );
}
